/**
 * Grid Combat Scenarios
 * Test scenarios specifying map generator configs + enemy placements for grid-based combat.
 */

import type { GeneratorConfig, RoomMetadata } from '@omen-quest/grid-map';
import type { GridEnemyDefinition } from './grid-types';

// ============================================================================
// TYPES
// ============================================================================

export interface GridCombatScenario {
  id: string;
  name: string;
  description: string;
  category: 'basic' | 'group' | 'boss' | 'dungeon';
  mapConfig: GeneratorConfig;
  /** Where players start. 'entrance' uses the BSP dungeon entrance room. */
  playerSpawn: { x: number; y: number } | 'entrance';
  /** Enemies with fixed or room-based placement */
  enemies: Array<GridEnemyDefinition | GridEnemyRoomPlacement>;
}

export interface GridEnemyRoomPlacement {
  name: string;
  description?: string;
  vitals: number;
  vitalsMax?: number;
  resolve?: number;
  resolveMax?: number;
  tags: string[];
  defense?: number;
  /** Place in a random room (resolved at runtime from RoomMetadata) */
  room: 'random' | 'farthest' | 'entrance';
  // startX/startY resolved at runtime
}

export function isRoomPlacement(e: GridEnemyDefinition | GridEnemyRoomPlacement): e is GridEnemyRoomPlacement {
  return 'room' in e;
}

// ============================================================================
// SCENARIOS
// ============================================================================

export const GRID_COMBAT_SCENARIOS: GridCombatScenario[] = [
  // ---- BASIC ----
  {
    id: 'arena_duel',
    name: 'Arena Duel',
    description: 'One-on-one combat in an open arena with pillars for cover.',
    category: 'basic',
    mapConfig: {
      type: 'arena',
      width: 20,
      height: 20,
      seed: 42,
      pillarSpacing: 5,
    },
    playerSpawn: { x: 3, y: 10 },
    enemies: [
      {
        name: 'Arena Champion',
        description: 'A battle-hardened gladiator',
        vitals: 10,
        tags: ['tactical', 'armored', 'strong'],
        defense: 1,
        startX: 16,
        startY: 10,
      },
    ],
  },
  {
    id: 'corridor_fight',
    name: 'Corridor Fight',
    description: 'Enemies blocking a narrow dungeon corridor.',
    category: 'basic',
    mapConfig: {
      type: 'bsp_dungeon',
      width: 30,
      height: 20,
      seed: 101,
      minRoomSize: 4,
      maxRoomSize: 8,
      maxDepth: 3,
      dungeonTheme: 'fortress',
    },
    playerSpawn: 'entrance',
    enemies: [
      {
        name: 'Corridor Guard',
        vitals: 8,
        tags: ['defensive', 'armored'],
        room: 'random',
      },
      {
        name: 'Corridor Archer',
        vitals: 5,
        tags: ['tactical', 'cunning'],
        room: 'random',
      },
    ],
  },

  // ---- GROUP ----
  {
    id: 'dungeon_ambush',
    name: 'Dungeon Ambush',
    description: 'Three enemies lurking in different rooms of a dungeon.',
    category: 'group',
    mapConfig: {
      type: 'bsp_dungeon',
      width: 40,
      height: 30,
      seed: 202,
      minRoomSize: 5,
      maxRoomSize: 10,
      maxDepth: 4,
      dungeonTheme: 'crypt',
    },
    playerSpawn: 'entrance',
    enemies: [
      {
        name: 'Crypt Lurker',
        vitals: 7,
        tags: ['aggressive', 'quick'],
        room: 'random',
      },
      {
        name: 'Bone Archer',
        vitals: 5,
        tags: ['tactical', 'cunning'],
        room: 'random',
      },
      {
        name: 'Tomb Guardian',
        vitals: 9,
        tags: ['defensive', 'armored', 'strong'],
        defense: 1,
        room: 'farthest',
      },
    ],
  },
  {
    id: 'arena_melee',
    name: 'Arena Melee',
    description: 'Four-way brawl in an open arena.',
    category: 'group',
    mapConfig: {
      type: 'arena',
      width: 24,
      height: 24,
      seed: 303,
      pillarSpacing: 6,
    },
    playerSpawn: { x: 4, y: 12 },
    enemies: [
      { name: 'Pit Fighter', vitals: 7, tags: ['aggressive', 'brute'], startX: 19, startY: 5 },
      { name: 'Knife Dancer', vitals: 5, tags: ['tactical', 'agile', 'quick'], startX: 19, startY: 19 },
      { name: 'Shield Bearer', vitals: 9, tags: ['defensive', 'armored', 'guardian'], defense: 1, startX: 12, startY: 19 },
    ],
  },

  // ---- BOSS ----
  {
    id: 'boss_chamber',
    name: 'Boss Chamber',
    description: 'A powerful boss with two minions in a large dungeon room.',
    category: 'boss',
    mapConfig: {
      type: 'bsp_dungeon',
      width: 35,
      height: 25,
      seed: 404,
      minRoomSize: 6,
      maxRoomSize: 12,
      maxDepth: 3,
      dungeonTheme: 'lair',
    },
    playerSpawn: 'entrance',
    enemies: [
      {
        name: 'Dungeon Lord',
        description: 'A massive armored beast that rules this lair',
        vitals: 20,
        resolve: 10,
        resolveMax: 10,
        tags: ['boss', 'heavily_armored', 'brute', 'aggressive'],
        defense: 2,
        room: 'farthest',
      },
      {
        name: 'Lair Warden',
        vitals: 8,
        tags: ['defensive', 'armored'],
        defense: 1,
        room: 'random',
      },
      {
        name: 'Lair Warden',
        vitals: 8,
        tags: ['defensive', 'armored'],
        defense: 1,
        room: 'random',
      },
    ],
  },

  // ---- DUNGEON ----
  {
    id: 'cave_crawl',
    name: 'Cave Crawl',
    description: 'Organic cave system with lurking creatures.',
    category: 'dungeon',
    mapConfig: {
      type: 'cave',
      width: 30,
      height: 30,
      seed: 505,
      fillProbability: 0.45,
      smoothIterations: 5,
    },
    playerSpawn: { x: 5, y: 15 },
    enemies: [
      { name: 'Cave Spider', vitals: 4, tags: ['aggressive', 'quick'], startX: 20, startY: 10 },
      { name: 'Cave Spider', vitals: 4, tags: ['aggressive', 'quick'], startX: 22, startY: 18 },
      { name: 'Spider Matriarch', vitals: 12, tags: ['tactical', 'strong', 'leader'], startX: 25, startY: 15 },
    ],
  },
];

// ============================================================================
// HELPERS
// ============================================================================

export function getGridScenarioById(id: string): GridCombatScenario | undefined {
  return GRID_COMBAT_SCENARIOS.find(s => s.id === id);
}

export function getGridScenariosByCategory(): Record<string, GridCombatScenario[]> {
  const grouped: Record<string, GridCombatScenario[]> = {};
  for (const scenario of GRID_COMBAT_SCENARIOS) {
    if (!grouped[scenario.category]) grouped[scenario.category] = [];
    grouped[scenario.category].push(scenario);
  }
  return grouped;
}

/**
 * Resolve room-based enemy placements to concrete (x, y) positions.
 * Uses the map's RoomMetadata to place enemies in appropriate rooms.
 */
export function resolveEnemyPlacements(
  scenario: GridCombatScenario,
  rooms: RoomMetadata[],
  entranceRoom?: RoomMetadata,
): GridEnemyDefinition[] {
  const resolved: GridEnemyDefinition[] = [];
  const usedRooms = new Set<number>();
  if (entranceRoom) usedRooms.add(entranceRoom.roomId);

  // Sort rooms by distance from entrance for "farthest" placement
  const sortedByDistance = entranceRoom
    ? [...rooms].sort((a, b) => {
        const distA = Math.abs(a.x - entranceRoom.x) + Math.abs(a.y - entranceRoom.y);
        const distB = Math.abs(b.x - entranceRoom.x) + Math.abs(b.y - entranceRoom.y);
        return distB - distA; // Descending: farthest first
      })
    : rooms;

  for (const enemy of scenario.enemies) {
    if (!isRoomPlacement(enemy)) {
      resolved.push(enemy);
      continue;
    }

    let room: RoomMetadata | undefined;

    switch (enemy.room) {
      case 'entrance':
        room = entranceRoom;
        break;
      case 'farthest':
        room = sortedByDistance.find(r => !usedRooms.has(r.roomId));
        break;
      case 'random': {
        const available = rooms.filter(r => !usedRooms.has(r.roomId));
        if (available.length > 0) {
          room = available[Math.floor(Math.random() * available.length)];
        }
        break;
      }
    }

    if (room) {
      usedRooms.add(room.roomId);
      // Place in center of room
      const cx = Math.floor(room.x + room.width / 2);
      const cy = Math.floor(room.y + room.height / 2);
      resolved.push({
        name: enemy.name,
        description: enemy.description,
        vitals: enemy.vitals,
        vitalsMax: enemy.vitalsMax,
        resolve: enemy.resolve,
        resolveMax: enemy.resolveMax,
        tags: enemy.tags,
        defense: enemy.defense,
        startX: cx,
        startY: cy,
      });
    }
  }

  return resolved;
}
